import { Button } from "@progress/kendo-react-buttons";
import { Field, FormRenderProps } from "@progress/kendo-react-form";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import { useInternationalization } from "@progress/kendo-react-intl";
import { Error, Label } from "@progress/kendo-react-labels";
import { TabStrip, TabStripTab } from "@progress/kendo-react-layout";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { useCallback, useEffect, useRef, useState } from "react";
import { AccumulatorQuoteCard } from ".";
import { PricingHistoryDialog, QuoteExpiry } from "..";
import accumulatorApi from "../../../api/accumulator";
import contractsApi from "../../../api/contracts";
import { IApiResults, useApi, useFetch, useFetchWhen } from "../../../hooks/useApi";
import { useUserContext } from "../../../hooks/useUserContext";
import DateUtility from "../../../utilities/dateUtilities";
import MathUtility from "../../../utilities/mathUtilities";
import { FormNumericTextBox } from "../../form";
import { LoadingIndicator } from "../../LoadingIndicator";
import { Slider } from "../../slider/Slider";
import { exportAccumulatorQuotePdf } from "./accumulatorQuotePdfExport";

interface IAccumulatorQuoteProps {
  formRenderProps: FormRenderProps;
  product: any;
  lookups: any;
  values: any;
  marketStatsResults: IApiResults;
}

export const AccumulatorQuoteStep = ({
  formRenderProps,
  product,
  lookups,
  values,
  marketStatsResults,
}: IAccumulatorQuoteProps) => {
  const { commodity, instrument } = product;
  const { context: user } = useUserContext();
  const formatter = useInternationalization();

  const saveDetailsApi = useApi(accumulatorApi.saveQuoteDetails);
  const getQuoteApi = useApi(accumulatorApi.getQuote);

  const existingQuoteId = formRenderProps.valueGetter("quoteId") as number;
  const quoteDetailsResult = useFetchWhen(
    accumulatorApi.getQuoteDetails,
    existingQuoteId > 0,
    existingQuoteId,
  );

  const [loading, setLoading] = useState(true);
  const [showChart, setShowChart] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [quoteId, setQuoteId] = useState(existingQuoteId);
  const [expiration, setExpiration] = useState<Date | null>(null);
  const [quote, setQuote] = useState<any | null>(null);

  const [fields, setFields] = useState<any[]>([]);
  const [underlying, setUnderlying] = useState(0);
  const [knockOutLevel, setKnockOutLevel] = useState<number | null>(null);
  const [strike, setStrike] = useState<number | null>(null);
  const [sliderValues, setSliderValues] = useState<number[]>([]);
  const [ticks, setTicks] = useState<number[]>([]);
  const [isSavingInitialValues, setIsSavingInitialValues] = useState(false);
  const [selectedPricingId, setSelectedPricingId] = useState<number | null>(null);
  const [isSavingGridSelection, setIsSavingGridSelection] = useState(false);

  // Prevents the initial saveQuoteDetails from firing more than once when both
  // contractsData and quoteDetailsResult arrive independently.
  const initialSaveFiredRef = useRef(false);

  const instrumentType = lookups?.types.find((d: any) => d.value === values.instrumentTypeId);
  const isProducer = instrumentType?.text === "Producer";
  const isRiskTaker = user.roles.isRiskTaker;
  const gridResults =
    getQuoteApi.data?.gridResults?.map((item: any) => ({
      ...item,
      unitPrice: !isRiskTaker && item.unitPrice < 0 ? 0 : item.unitPrice,
    })) ?? [];

  const handleExportPdf = (e: any) =>
    exportAccumulatorQuotePdf(
      {
        quote,
        quoteId,
        product,
        lookups,
        values,
        formRenderProps,
        instrumentType,
        gridResults,
        underlying,
        isRiskTaker,
      },
      e,
    );

  const contract = DateUtility.formatDateYMD(formRenderProps.valueGetter("contract"));
  const start = DateUtility.formatDateYMD(formRenderProps.valueGetter("start"));
  const expiry = DateUtility.formatDateYMD(formRenderProps.valueGetter("expiryDate"));
  const settlement = DateUtility.formatDateYMD(formRenderProps.valueGetter("settlement"));

  const { data: contractsData, error: contractsError } = useFetch(contractsApi.getIntraDayPrices, {
    tickerCode: commodity.abbreviation,
    instrumentType: instrument.id,
    startDate: contract,
    endDate: contract,
  });

  useEffect(() => {
    setKnockOutLevel(null);
    setStrike(null);
    setQuote(null);
    setQuoteId(formRenderProps.valueGetter("quoteId"));
    setExpiration(null);
    formRenderProps.onChange("knockOutLevel", { value: null });
    formRenderProps.onChange("strike", { value: null });
    formRenderProps.onChange("quote", { value: null });
    formRenderProps.onChange("expiry", { value: "" });
  }, []);

  // Fire the initial saveQuoteDetails once both contracts prices and (for existing quotes)
  // quote details are available, so that the restored selectedPricingId is included in the save.
  useEffect(() => {
    if (!contractsData?.prices) return;
    if (existingQuoteId > 0 && !quoteDetailsResult.data && !quoteDetailsResult.error) return;
    if (initialSaveFiredRef.current) return;

    initialSaveFiredRef.current = true;

    const underlying = MathUtility.round(contractsData.prices[contract]);
    setUnderlying(underlying);

    const maxTicks = 20;
    const min = MathUtility.round(underlying * 0.8, 2);
    const max = MathUtility.round(underlying * 1.2, 2);
    const desiredInterval = (max - min) / maxTicks;
    const interval = Math.max(0.01, Math.round(desiredInterval * 100) / 100);

    const sliderTicks: number[] = [];
    for (let v = min; v <= max + 1e-9; v = MathUtility.round(v + interval, 2)) {
      sliderTicks.push(v);
    }
    setTicks(sliderTicks);

    let margin = 0.01;
    const factor = isProducer ? 1 : -1;
    const koLimit = underlying - factor * margin;
    const stLimit = underlying + factor * margin;

    margin = underlying * 0.2;
    const knockOutField = {
      field: "knockOutLevel",
      label: "Knock-Out Level",
      min: isProducer ? underlying - margin : koLimit,
      max: isProducer ? koLimit : underlying + margin,
      onChange: (value: any) => {
        const numValue =
          value === "" || value === null || isNaN(Number(value)) ? null : Number(value);
        setKnockOutLevel(numValue);
        formRenderProps.onChange("knockOutLevel", { value: numValue });
      },
    };
    const strikeField = {
      field: "strike",
      label: "Strike",
      min: isProducer ? stLimit : underlying - margin,
      max: isProducer ? underlying + margin : stLimit,
      onChange: (value: any) => {
        const numValue =
          value === "" || value === null || isNaN(Number(value)) ? null : Number(value);
        setStrike(numValue);
        formRenderProps.onChange("strike", { value: numValue });
      },
    };
    setFields(isProducer ? [knockOutField, strikeField] : [strikeField, knockOutField]);

    const restoredSpId = quoteDetailsResult.data?.quote?.selectedPricingId ?? null;
    if (restoredSpId !== null) setSelectedPricingId(restoredSpId);
    saveQuoteDetails(null, null, restoredSpId);
  }, [contractsData, quoteDetailsResult.data, quoteDetailsResult.error]);

  useEffect(() => {
    formRenderProps.onChange("knockOutLevel", { value: knockOutLevel });
    formRenderProps.onChange("strike", { value: strike });
    setSliderValues([knockOutLevel || 0, strike || 0]);
  }, [knockOutLevel, strike]);

  useEffect(() => {
    if (saveDetailsApi.data?.quoteId) {
      const value = saveDetailsApi.data?.quoteId;
      formRenderProps.onChange("quoteId", { value: value });
      setQuoteId(value);

      if (!isSavingInitialValues && !isSavingGridSelection) {
        getQuote(value);
      } else {
        setIsSavingInitialValues(false);
        setIsSavingGridSelection(false);
      }
    }
  }, [saveDetailsApi.data]);

  useEffect(() => {
    if (!getQuoteApi.data) return;
    const {
      quoteExpiry,
      quote,
      gridResults: responseGridResults,
      selectedPricingId: responseSelectedPricingId,
    } = getQuoteApi.data;

    if (quoteExpiry) setExpiration(new Date(Date.parse(quoteExpiry)));

    if (quote) {
      const applyUnitPrice = (item: any) => ({
        ...item,
        unitPrice: !isRiskTaker && item.unitPrice < 0 ? 0 : item.unitPrice,
      });

      // If a grid selection was previously saved and still exists in the new results, restore it.
      const savedRow = responseSelectedPricingId
        ? (responseGridResults ?? []).find(
            (item: any) => item.pricingId === responseSelectedPricingId,
          )
        : null;

      const activeQuote = applyUnitPrice(savedRow ?? quote);

      setQuote(activeQuote);
      formRenderProps.onChange("quote", { value: activeQuote });

      const hadNullValues = !knockOutLevel || !strike;

      if (!knockOutLevel || !strike) {
        setKnockOutLevel(MathUtility.round(activeQuote.knockOutLevel, 2));
        setStrike(MathUtility.round(activeQuote.strike, 2));
      }

      if (savedRow) {
        setSelectedPricingId(responseSelectedPricingId);
      }

      if (hadNullValues && activeQuote.knockOutLevel && activeQuote.strike) {
        setIsSavingInitialValues(true);
        saveQuoteDetails(
          MathUtility.round(activeQuote.knockOutLevel, 2),
          MathUtility.round(activeQuote.strike, 2),
          savedRow ? responseSelectedPricingId : undefined,
        );
      }
    }
  }, [getQuoteApi.data]);

  useEffect(() => {
    const isLoading = saveDetailsApi.loading || getQuoteApi.loading || quoteDetailsResult.loading;
    setLoading(isLoading);
    formRenderProps.onChange("loaded", { value: !isLoading });
  }, [saveDetailsApi.loading, getQuoteApi.loading, quoteDetailsResult.loading]);

  useEffect(() => {
    if (saveDetailsApi.error || getQuoteApi.error) onExpiryChange("");
  }, [saveDetailsApi.error, getQuoteApi.error]);

  const saveQuoteDetails = useCallback(
    async (ko?: number | null, st?: number | null, spId?: number | null) => {
      const saveDetails = {
        companyId: product.company.id,
        configurationId: formRenderProps.valueGetter("configurationId"),
        quoteId: quoteId,
        quoteName: formRenderProps.valueGetter("quoteName"),
        contractDate: contract,
        startDate: start,
        expiryDate: expiry,
        settlementDate: settlement,
        structureId: 21,
        settlementTypeId: values.settlementTypeId,
        doubleUpTypeId: values.doubleUpTypeId,
        instrumentTypeId: values.instrumentTypeId,
        knockOutTypeId: values.knockOutTypeId,
        volume: formRenderProps.valueGetter("volume"),
        knockOutLevel: ko !== undefined ? ko : knockOutLevel,
        strike: st !== undefined ? st : strike,
        selectedPricingId: spId !== undefined ? spId : selectedPricingId,
      };
      saveDetailsApi.request(saveDetails);
    },
    [quoteId, contract, start, settlement, knockOutLevel, strike, selectedPricingId, saveDetailsApi],
  );

  const getQuote = useCallback(async (quoteId: number) => {
    if (quoteId) getQuoteApi.request(quoteId);
  }, []);

  const onExpiryChange = (expiry: string) => {
    formRenderProps.onChange("expiry", { value: expiry });
  };

  const onSliderChange = (newValues: number[]) => {
    const ko = MathUtility.round(newValues[0], 2);
    const st = MathUtility.round(newValues[1], 2);

    const koField = isProducer ? fields[0] : fields[1];
    if (ko <= koField.max && ko >= koField.min) setKnockOutLevel(ko);

    const stField = isProducer ? fields[1] : fields[0];
    if (st <= stField.max && st >= stField.min) setStrike(st);
  };

  const onSliderDrag = (newValues: number[]) => {
    const ko = MathUtility.round(newValues[0], 2);
    const koField = isProducer ? fields[0] : fields[1];
    if (ko <= koField.max && ko >= koField.min) setKnockOutLevel(ko);

    const st = MathUtility.round(newValues[1], 2);
    const stField = isProducer ? fields[1] : fields[0];
    if (st <= stField.max && st >= stField.min) setStrike(st);
  };

  const handleRefresh = () => {
    saveQuoteDetails(knockOutLevel, strike);

    if (selectedTab === 1) {
      setSelectedTab(0);
    }
  };

  const handleSelectionChange = (event: any) => {
    const selectedId = Object.keys(event.select)[0];
    if (selectedId) {
      const selectedRow = gridResults.find((item: any) => item.pricingId === parseInt(selectedId));

      if (selectedRow) {
        const ko = MathUtility.round(selectedRow.knockOutLevel, 2);
        const st = MathUtility.round(selectedRow.strike, 2);
        setQuote(selectedRow);
        formRenderProps.onChange("quote", { value: selectedRow });
        setKnockOutLevel(ko);
        setStrike(st);
        setSelectedPricingId(selectedRow.pricingId);
        setIsSavingGridSelection(true);
        saveQuoteDetails(ko, st, selectedRow.pricingId);
      }
    }
  };

  const handleGridRequote = (e: any) => {
    e?.preventDefault?.();

    setKnockOutLevel(null);
    setStrike(null);
    setSelectedPricingId(null);

    formRenderProps.onChange("knockOutLevel", { value: null });
    formRenderProps.onChange("strike", { value: null });

    saveQuoteDetails(null, null, null);
  };

  return (
    <div className="stepper-form">
      <TabStrip selected={selectedTab} onSelect={(e) => setSelectedTab(e.selected)}>
        <TabStripTab title="Quote">
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "baseline",
                justifyContent: "space-between",
              }}
            >
              <div style={{ flexBasis: "35%" }}>
                <div
                  style={{
                    display: "flex",
                    alignContent: "start",
                    justifyContent: "space-around",
                  }}
                >
                  {fields.map((t) => (
                    <Field
                      key={t.field}
                      id={t.field}
                      name={t.field}
                      label={t.label}
                      component={FormNumericTextBox}
                      spinners={false}
                      format={"n2"}
                      min={t.min}
                      max={t.max}
                      onBlurOverride={(e: any) => t.onChange(e.target.value)}
                      style={{ maxWidth: 150 }}
                    />
                  ))}
                </div>
              </div>
              <div style={{ flexBasis: "50%" }}>
                <div>
                  <Label>Adjust Values</Label>
                  <Slider
                    options={{
                      name: "levelSlider",
                      values: sliderValues.map((v) => MathUtility.round(v, 2)),
                      min: MathUtility.round(underlying * 0.8, 2),
                      max: MathUtility.round(underlying * 1.2, 2),
                      staticValues: [underlying],
                      staticLabels: ["U"],
                      stepSize: 0.01,
                      ticks: ticks,
                    }}
                    onChange={onSliderChange}
                    onDrag={onSliderDrag}
                  />
                </div>
              </div>

              <div>
                <div className="utility-button-container">
                  <Tooltip anchorElement="target" position="bottom" parentTitle={true}>
                    <Button
                      size="small"
                      icon="arrow-rotate-cw"
                      themeColor="primary"
                      title="Refresh"
                      onClick={(e: any) => {
                        e.preventDefault();
                        handleRefresh();
                      }}
                      disabled={loading}
                      style={{ marginLeft: "5px" }}
                    />
                    <Button
                      togglable={true}
                      size="small"
                      icon="align-self-end-alt"
                      themeColor="primary"
                      title="View Chart"
                      onClick={(e: any) => {
                        e.preventDefault();
                        setShowChart(true);
                      }}
                    />
                  </Tooltip>
                </div>
              </div>
            </div>

            <Error>{contractsError}</Error>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                paddingTop: "1rem",
              }}
            >
              <div style={{ flexBasis: "100%" }}>
                <LoadingIndicator loading={loading} />
                {saveDetailsApi.error && <Error>{saveDetailsApi.error}</Error>}
                {getQuoteApi.error && <Error>{getQuoteApi.error}</Error>}

                {!loading && quote && (
                  <AccumulatorQuoteCard
                    title="Quote Details"
                    quote={quote}
                    commodity={commodity}
                    chartData={{
                      knockOutLevel,
                      strike,
                      underlying,
                      marketStatsResults,
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        </TabStripTab>

        <TabStripTab title="Grid">
          <div>
            {underlying > 0 && (
              <div style={{ textAlign: "center", marginBottom: "1rem" }}>
                <Label>Underlying Price: {formatter.formatNumber(underlying, "c4")}</Label>
              </div>
            )}
            <LoadingIndicator loading={loading} />
            {getQuoteApi.data?.gridResults && getQuoteApi.data.isGridResult && (
              <div>
                <Grid
                  data={gridResults}
                  dataItemKey="pricingId"
                  selectable={{
                    enabled: true,
                    mode: "single",
                  }}
                  onSelectionChange={handleSelectionChange}
                  select={quote?.pricingId ? { [quote.pricingId]: true } : undefined}
                  style={{ maxHeight: 300 }}
                >
                  <GridColumn field="pricingId" title="Pricing Id" />
                  <GridColumn field="unitPrice" title="Unit Price" format="{0:c3}" />
                  <GridColumn field="knockOutLevel" title="Knock-Out Level" format="{0:n4}" />
                  <GridColumn field="strike" title="Strike" format="{0:n4}" />
                  <GridColumn field="delta" title="Delta" format="{0:n2}" />
                </Grid>
              </div>
            )}
            <div
              style={{
                textAlign: "center",
                marginTop: "0.75rem",
                display: "flex",
                justifyContent: "center",
                gap: "0.5rem",
              }}
            >
              <Button
                icon="arrow-rotate-cw"
                themeColor="primary"
                title="Requote (clear strike & knock-out)"
                onClick={(e: any) => handleGridRequote(e)}
                disabled={loading}
              >
                Requote
              </Button>
              <Button
                icon="file-pdf"
                themeColor="primary"
                title="Export PDF"
                onClick={handleExportPdf}
                disabled={loading || !quote || gridResults.length === 0}
              >
                Export PDF
              </Button>
            </div>
            {(!getQuoteApi.data?.gridResults || !getQuoteApi.data.isGridResult) && !loading && (
              <div style={{ textAlign: "center", padding: "2rem", color: "#666" }}>
                To view a list of options, click requote. Note: This may change your current
                knockout and strike.
              </div>
            )}
          </div>
        </TabStripTab>
      </TabStrip>

      <div style={{ paddingTop: 15 }}>
        <QuoteExpiry
          expiration={expiration}
          loading={loading}
          onRefresh={handleRefresh}
          onExpire={onExpiryChange}
        />
      </div>

      {showChart && (
        <PricingHistoryDialog
          commodityName={commodity.name}
          apiResults={marketStatsResults}
          onCancel={() => setShowChart(false)}
        />
      )}
    </div>
  );
};
